import { Box, Stack, Typography } from "@mui/material";
import ReactFlow, {
    Background,
    Node,
    Edge,
    useReactFlow,
    ReactFlowProvider
} from "reactflow";
import "reactflow/dist/style.css";
import { Border, InversorNode, SmartLoggerNode, DJMTDJBTNode, TrafoNode, TrafoTitleNode, TitleNode, StringButton, EndPointNode } from "./DiagramNodes";
import { useContext, useEffect, useState } from "react";
import StringsModal from "./StringsModal";
import { PositionAndSizeCalculator } from "../utils";
import { MainContext } from "../../../main/context/MainContextProvider";
import CommandsModal from "./CommandsModal";
import { BreakerImage } from "./BreakerImage";
import { InverterImage } from "./InverterImage";

const nodeTypes = {
    Border: Border,
    Inversor: InversorNode,
    smartLogger: SmartLoggerNode,
    DJMTDJBT: DJMTDJBTNode,
    TrafoNode: TrafoNode,
    TrafoTitleNode: TrafoTitleNode,
    Title: TitleNode,
    StringButton: StringButton,
    EndPointNode: EndPointNode
};

export type InversorData = {
    name: string;
    efficiency: number | string;
    status: "on" | "off" | "notelemetry" | "unknown";
    strings?: InversorString[]
}

export type DiagramUgData = {
    ugName: string;
    ugNumber?: number;
    hasSmartLogger?: boolean
    inversoresData?: { commands: string[], inversors: InversorData[] },
    DJBTData?: {
        status: "on" | "off" | "notelemetry" | "unknown",
        disjuntorTag?: string
    },
    DJMTData?: {
        efficiency: number;
        status: "on" | "off" | "notelemetry" | "unknown";
        disjuntorTag?: string;
    },
    trafoData?: {
        firstTemperature: number;
        secondTemperature: number;
        thirdTemperature: number;
        hasTransformer?: boolean;
    },
    djbtdjmtCommands?: string[];
}

export type InversorString = { stringNumber: string, corrente: number | null }

export type ReleProtecaoData = {
    isActive: boolean;
    MWValue: number;
    KVValue: number;
    KWhValue: number;
};

export type DiagramSubstation = {
    hasTransformer?: boolean | null;
    hasSubstation?: boolean | null;
    hasDisjuntor?: boolean | null;
    disjuntorTag?: string | null;
    disjuntorStatus?: "on" | "off" | "notelemetry" | "unknown";
    commands: string[]
}

export type DiagramaUnifilarProps = {
    ugData?: DiagramUgData[];
    releProtecaoData: ReleProtecaoData;
    substation: DiagramSubstation;
    usinaIsMQTT?: boolean;
    usinaCode: string;
    usinaId: string;
}

function StatusImage({
    image,
    statusLabel,
}: {
    image: React.ReactNode;
    statusLabel: string;
}) {
    return (
        <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
            {image}
            <Typography>{statusLabel}</Typography>
        </Stack>
    );
}

function statusToStatusLabel(
    status: "on" | "off" | "notelemetry" | "unknown" | null | undefined,
    onLabel: string,
    offLabel: string,
) {
    switch (status) {
        case "on":
            return onLabel;
        case "off":
            return offLabel;
        case "notelemetry":
            return "Sem Telemetria";
        case "unknown":
        default:
            return "Desconhecido";
    }
}

function disjuntorStatusToLabel(
    status: "on" | "off" | "notelemetry" | "unknown" | null | undefined,
) {
    const statusLabel = statusToStatusLabel(status, "Fechado", "Aberto");
    return (
        <StatusImage
            image={<BreakerImage type={status ?? "unknown"} width={25} height={25} />}
            statusLabel={statusLabel}
        />
    );
}

function inversorStatusToLabel(
    status: "on" | "off" | "notelemetry" | "unknown" | null | undefined,
) {
    const statusLabel = statusToStatusLabel(status, "Ligado", "Desligado");
    return (
        <StatusImage
            image={
                <InverterImage type={status ?? "unknown"} width={25} height={25} />
            }
            statusLabel={statusLabel}
        />
    );
}

function DiagramContent(props: DiagramaUnifilarProps) {
    const { ugData = [], releProtecaoData, substation, usinaIsMQTT, usinaCode, usinaId } = props;

    const { selectedDashboardUsina } = useContext(MainContext);
    const { fitView } = useReactFlow();

    const calculator = new PositionAndSizeCalculator();

    let ugInversorsCount: Record<number, number> = {};

    ugData.map((ug, ugIndex) => {
        ugInversorsCount[ugIndex + 1] = ug.inversoresData?.inversors.length ?? 0;
    })

    calculator.SetInversorsCount(ugInversorsCount);

    const [clickedInversor, setClickedInversor] = useState<{ ugIndex: number, inversorIndex: number, strings?: InversorString[] } | null>(null);
    const [openStringsModal, setOpenStringsModal] = useState<boolean>(false);

    const onCloseStringsModal = () => setOpenStringsModal(false);

    const [nodes, setNodes] = useState<Node[]>([]);
    const [edges, setEdges] = useState<Edge[]>([]);
    var currentInversorsCount = 0;
    const [commandModalValues, setCommandModalValues] = useState<{
        areaCode: string,
        commands: string[],
        equipmentIdentifier?: string;
        type: "boolean" | "integer" | "real" | "trigger";
        usinaCode: string
        usinaId: string;
        statusLabel?: React.ReactNode;
    }>()

    useEffect(() => {
        let newNodes: Node[] = [];
        let newEdges: Edge[] = [];
        currentInversorsCount = 0;

        ugData.map((ug, ugIndex) => {
            const ugNode: Node = {
                id: `ug${ugIndex + 1}`,
                type: "Border",
                position: calculator.getUgBorderPosition(ugIndex, currentInversorsCount),
                data: { ugName: ug.ugName, width: calculator.getUgBorderWidth(ugIndex), height: 360 },
                draggable: false,
            };

            const smartLoggerNode: Node = {
                id: `ug${ugIndex + 1}-smartLogger`,
                type: "smartLogger",
                position: calculator.getSmartLoggerPosition(ugIndex, currentInversorsCount),
                data: {},
                draggable: false,
            };

            const smartLoggerTitleNode: Node = {
                id: `ug${ugIndex + 1}-smartLoggerTitle`,
                type: "Title",
                position: calculator.getSmartLoggerTitlePosition(ugIndex, currentInversorsCount),
                data: { title: "SmartLogger" },
                draggable: false,
            };

            const DJBTNode: Node = {
                id: `ug${ugIndex + 1}-DJBT`,
                type: "DJMTDJBT",
                position: calculator.getDJBTPosition(ugIndex, currentInversorsCount, ug.trafoData?.hasTransformer ? 150 : 95),
                data: {
                    type: ug.DJBTData?.status,
                    isBottomSource: ug.hasSmartLogger ? false : true,
                    isTopSource: ug.trafoData?.hasTransformer || (!ug.trafoData?.hasTransformer && substation.hasSubstation) ? false : true,
                    onClick: () => {
                        setCommandModalValues({
                            areaCode: ug.ugName,
                            commands: ug.djbtdjmtCommands ?? [],
                            equipmentIdentifier: "QGBT",
                            usinaCode: usinaCode,
                            usinaId,
                            type: "trigger",
                            statusLabel: disjuntorStatusToLabel(ug.DJBTData?.status),
                        })
                        setOpenCommandModal(true)
                    },
                },
                draggable: false,
            };

            const DJBTTitleNode: Node = {
                id: `ug${ugIndex + 1}-djbtTitle`,
                type: "Title",
                position: calculator.getDJBTTitlePosition(ugIndex, currentInversorsCount, ug.trafoData?.hasTransformer ? 160 : 105),
                data: { title: ug.DJBTData?.disjuntorTag ?? "DJBT" },
                draggable: false,
            };

            const TrafoNode: Node = {
                id: `ug${ugIndex + 1}-trafo`,
                type: "TrafoNode",
                position: calculator.getTrafoPosition(ugIndex, currentInversorsCount),
                data: {},
                draggable: false,
            };

            const TrafoTitleNode: Node = {
                id: `ug${ugIndex + 1}-trafoTitle`,
                type: "TrafoTitleNode",
                position: calculator.getTrafoTitlePosition(ugIndex, currentInversorsCount),
                data: {
                    firstTemperature: ug.trafoData?.firstTemperature ?? 0,
                    secondTemperature: ug.trafoData?.secondTemperature ?? 0,
                    thirdTemperature: ug.trafoData?.thirdTemperature ?? 0,
                },
                draggable: false,
            };

            const DJMTNode: Node = {
                id: `ug${ugIndex + 1}-DJMT`,
                type: "DJMTDJBT",
                position: calculator.getDJMTPosition(ugIndex, currentInversorsCount),
                data: {
                    type: ug.DJMTData?.status,
                    onClick: () => {
                        setCommandModalValues({
                            areaCode: ug.ugName,
                            commands: ug.djbtdjmtCommands ?? [],
                            equipmentIdentifier: "SUB",
                            usinaCode: usinaCode,
                            usinaId,
                            type: "trigger",
                            statusLabel: disjuntorStatusToLabel(ug.DJMTData?.status),
                        })
                        setOpenCommandModal(true)
                    }
                },
                draggable: false,
            };

            const DJMTTitleNode: Node = {
                id: `ug${ugIndex + 1}-djmtTitle`,
                type: "Title",
                position: calculator.getDJMTTitlePosition(ugIndex, currentInversorsCount),
                data: { title: ug.DJMTData?.disjuntorTag ?? "DJMT" },
                draggable: false,
            };

            const DJMTValueNode: Node = {
                id: `ug${ugIndex + 1}-djmtValue`,
                type: "Title",
                position: calculator.getDJMTValuePosition(ugIndex, currentInversorsCount),
                data: { title: `${ug.DJMTData?.efficiency} MW` },
                draggable: false,
            };

            newNodes.push(ugNode, DJBTNode, DJBTTitleNode);

            if (ug.hasSmartLogger) {
                newNodes.push(smartLoggerNode, smartLoggerTitleNode);
            }

            if (ug.trafoData?.hasTransformer) {
                newNodes.push(TrafoNode);
                newNodes.push(DJMTNode, DJMTTitleNode);
            }

            if (usinaIsMQTT) {
                newNodes.push(TrafoTitleNode);

                if (ug.trafoData?.hasTransformer) {
                    newNodes.push(DJMTValueNode);
                }
            }

            ug.inversoresData?.inversors.map((inv, inversorIndex) => {
                currentInversorsCount++;
                const invNode: Node =
                {
                    id: `ug${ugIndex + 1}-inv${inversorIndex + 1}`,
                    type: "Inversor",
                    position: calculator.getInversorAndStringButtonPosition(ugIndex, inversorIndex, 280),
                    data: {
                        type: inv.status,
                        name: inv.name,
                        efficiency: inv.efficiency,
                        onClick: () => {
                            setCommandModalValues({
                                areaCode: ug.ugName,
                                commands: ug.inversoresData?.commands ?? [],
                                equipmentIdentifier: inv.name,
                                usinaCode: usinaCode,
                                usinaId,
                                type: "trigger",
                                statusLabel: inversorStatusToLabel(inv.status),
                            })
                            setOpenCommandModal(true)
                        }
                    },
                    draggable: false,
                }

                const strgNode: Node =
                {
                    id: `ug${ugIndex + 1}-inv${inversorIndex + 1}-stringButton`,
                    type: "StringButton",
                    position: calculator.getInversorAndStringButtonPosition(ugIndex, inversorIndex, 358),
                    data: { onClick: () => { setClickedInversor({ ugIndex: ugIndex + 1, inversorIndex: inversorIndex + 1, strings: inv.strings }); setOpenStringsModal(true); } },
                    draggable: false,
                }

                newNodes = [...(newNodes ?? []), invNode, ...(inv.strings && inv.strings.length > 0 ? [strgNode] : [])];
            })

        })

        const substationBorderNode = {
            id: "substationBorder",
            type: "Border",
            position: calculator.getSubstationBorderPosition(),
            data: { ugName: "SUBSTATION", width: 120, height: 110 },
            draggable: false
        }

        const substationTrafoNode: Node = {
            id: `substationTrafo`,
            type: "TrafoNode",
            position: calculator.getSubstationTrafoPosition(),
            data: {
                isTopTarget: !substation.hasDisjuntor,
            },
            draggable: false,
        };

        const substationTrafoTitleNode: Node = {
            id: `substationTrafoTitle`,
            type: "Title",
            position: calculator.getSubstationTrafoTitlePosition(),
            data: { title: "Trafo" },
            draggable: false,
        };

        const substationDisjuntorNode: Node =
        {
            id: `substationDisjuntor`,
            type: "DJMTDJBT",
            position: calculator.getSubstationDisjuntorPosition(!!substation.hasTransformer),
            data: {
                type: substation.disjuntorStatus,
                isBottomSource: substation.hasTransformer ? false : true, onClick: () => {
                    setCommandModalValues({
                        areaCode: "SUB",
                        commands: substation.commands,
                        usinaCode: usinaCode,
                        usinaId,
                        type: "trigger",
                        statusLabel: disjuntorStatusToLabel(substation.disjuntorStatus),
                    })
                    setOpenCommandModal(true)
                }
            },
            draggable: false
        };

        const substationDisjuntorTitleNode: Node = {
            id: `substationDisjuntorTitle`,
            type: "Title",
            position: calculator.getSubstationDisjuntorTitlePosition(!!substation.hasTransformer),
            data: { title: substation.disjuntorTag ?? "Disjuntor" },
            draggable: false,
        };

        const ReleProtecaoNode: Node =
        {
            id: `releProtecao`,
            type: "EndPointNode",
            position: calculator.getReleProtecaoPosition(substation.hasSubstation ? -190 : -50),
            data: {},
            draggable: false
        }

        const ReleProtecaoMWValueNode: Node =
        {
            id: `mwValueReleProtecao`,
            type: "Title",
            position: calculator.getReleProtecaoValuesPosition(substation.hasSubstation ? -190 : -50),
            data: { title: `${releProtecaoData.MWValue} MW` },
            draggable: false
        }

        const ReleProtecaoKVValueNode: Node =
        {
            id: `kvValueReleProtecao`,
            type: "Title",
            position: calculator.getReleProtecaoValuesPosition(substation.hasSubstation ? -180 : -40),
            data: { title: `${releProtecaoData.KVValue} KV` },
            draggable: false
        }

        const ReleProtecaoMwValueNode: Node =
        {
            id: `kwhValueReleProtecao`,
            type: "Title",
            position: calculator.getReleProtecaoValuesPosition(substation.hasSubstation ? -170 : -30),
            data: { title: `${releProtecaoData.KWhValue} KWh` },
            draggable: false
        }

        newNodes.push(ReleProtecaoNode);

        if (usinaIsMQTT) {
            newNodes.push(
                ReleProtecaoMWValueNode,
                ReleProtecaoKVValueNode,
                ReleProtecaoMwValueNode
            );
        }

        if (substation.hasSubstation) {
            newNodes.push(substationBorderNode);

            if (substation.hasTransformer) {
                newNodes.push(substationTrafoNode, substationTrafoTitleNode);
            }

            if (substation.hasDisjuntor) {
                newNodes.push(substationDisjuntorNode, substationDisjuntorTitleNode);
            }
        }

        ugData.map((ug, ugIndex) => {

            const releProtecaoToDJMTEdge = {
                id: `ug${ugIndex + 1}-releProtecaoToDJMT`,
                source: `releProtecao`,
                sourceHandle: "releProtecaoBottom",
                target: `ug${ugIndex + 1}-DJMT`,
                targetHandle: "DJMTDJBTTop",
            };


            const releProtecaoToDJBTEdge = {
                id: `ug${ugIndex + 1}-releProtecaoToDJMT`,
                source: `releProtecao`,
                sourceHandle: "releProtecaoBottom",
                target: `ug${ugIndex + 1}-DJBT`,
                targetHandle: "DJMTDJBTTop",
            };

            const smartLoggerToDJBTEdge = {
                id: `ug${ugIndex + 1}-smartLoggerToDJBT`,
                source: `ug${ugIndex + 1}-smartLogger`,
                sourceHandle: "smartLoggerTop",
                target: `ug${ugIndex + 1}-DJBT`,
                targetHandle: "DJMTDJBTBottom",
            };

            const substationTrafoToDJMTEdge = {
                id: `ug${ugIndex + 1}-substationTrafoToDJMT`,
                source: `substationTrafo`,
                sourceHandle: "trafoBottom",
                target: `ug${ugIndex + 1}-DJMT`,
                targetHandle: "DJMTDJBTTop",
            };

            const trafoToDJBTEdge = {
                id: `ug${ugIndex + 1}-trafoToDJBT`,
                source: `ug${ugIndex + 1}-trafo`,
                sourceHandle: "trafoBottom",
                target: `ug${ugIndex + 1}-DJBT`,
                targetHandle: "DJMTDJBTTop",
            };

            const trafoToDJMTEdge = {
                id: `ug${ugIndex + 1}-trafoToDJMT`,
                source: `ug${ugIndex + 1}-trafo`,
                sourceHandle: "trafoTop",
                target: `ug${ugIndex + 1}-DJMT`,
                targetHandle: "DJMTDJBTBottom",
            };

            const DJBTToDJMTEdge = {
                id: `ug${ugIndex + 1}-trafoToDJMT`,
                source: `ug${ugIndex + 1}-DJBT`,
                sourceHandle: "DJMTDJBTTop",
                target: `ug${ugIndex + 1}-DJMT`,
                targetHandle: "DJMTDJBTBottom",
            }

            const substationTrafoToUgTrafoEdge = {
                id: `ug${ugIndex + 1}-substationTrafoToUgTrafo`,
                source: `substationTrafo`,
                sourceHandle: "trafoBottom",
                target: `ug${ugIndex + 1}-trafo`,
                targetHandle: "trafoTop",
            };

            const substationTrafoToUgDJBTEdge = {
                id: `ug${ugIndex + 1}-substationTrafoToUgDJBT`,
                source: `substationTrafo`,
                sourceHandle: "trafoBottom",
                target: `ug${ugIndex + 1}-DJBT`,
                targetHandle: "DJMTDJBTTop",
            };

            if (ug.trafoData?.hasTransformer) {
                newEdges.push(trafoToDJBTEdge, trafoToDJMTEdge);
            } else {
                newEdges.push(DJBTToDJMTEdge);
            }

            if (substation.hasSubstation) {
                if (ug.trafoData?.hasTransformer) {
                    if (substation.hasTransformer) {
                        newEdges.push(substationTrafoToUgTrafoEdge);
                        newEdges.push(substationTrafoToDJMTEdge);
                    } else {
                        newEdges.push(releProtecaoToDJBTEdge);
                    }
                } else {
                    if (substation.hasTransformer) {
                        newEdges.push(substationTrafoToUgDJBTEdge);
                    } else {
                        newEdges.push(releProtecaoToDJBTEdge);
                    }
                }
            } else {
                newEdges.push(
                    ug.trafoData?.hasTransformer
                        ? releProtecaoToDJMTEdge
                        : releProtecaoToDJBTEdge
                );
            }

            if (ug.hasSmartLogger) {
                newEdges.push(smartLoggerToDJBTEdge);
            }

            ug.inversoresData?.inversors.map((_inv, inversorIndex) => {
                const invWithSmartLoggerEdge: Edge =
                {
                    id: `ug${ugIndex + 1}-inv${inversorIndex + 1}-edge`,
                    source: `ug${ugIndex + 1}-smartLogger`,
                    sourceHandle: "smartLoggerBottom",
                    target: `ug${ugIndex + 1}-inv${inversorIndex + 1}`,
                    targetHandle: "InversorTop",
                }

                const invWithoutSmartLoggerEdge: Edge =
                {
                    id: `ug${ugIndex + 1}-inv${inversorIndex + 1}-edge`,
                    source: `ug${ugIndex + 1}-DJBT`,
                    sourceHandle: "releProtecaoBottom",
                    target: `ug${ugIndex + 1}-inv${inversorIndex + 1}`,
                    targetHandle: "InversorTop",
                }

                newEdges = [...(newEdges ?? []), ...(ug.hasSmartLogger ? [invWithSmartLoggerEdge] : [invWithoutSmartLoggerEdge])];
            })
        })

        const substationDJMTTosubstationTrafoEdge = {
            id: `substationDJMTTosubstationTrafo`,
            source: `substationTrafo`,
            sourceHandle: "trafoTop",
            target: `substationDisjuntor`,
            targetHandle: "DJMTDJBTBottom",
        };

        const releProtecaoTosubstationDJMTEdge = {
            id: `releProtecaoTosubstationDJMT`,
            source: `releProtecao`,
            sourceHandle: "releProtecaoBottom",
            target: `substationDisjuntor`,
            targetHandle: "DJMTDJBTTop",
        };

        const releProtecaoTosubstationTrafoEdge = {
            id: `releProtecaoTosubstationTrafo`,
            source: `releProtecao`,
            sourceHandle: "releProtecaoBottom",
            target: `substationTrafo`,
            targetHandle: "trafoTop",
        };

        if (substation.hasSubstation && substation.hasDisjuntor) {
            newEdges = [...(newEdges ?? []), substationDJMTTosubstationTrafoEdge, releProtecaoTosubstationDJMTEdge];
        } else if (substation.hasSubstation && substation.hasTransformer && !substation.hasDisjuntor) {
            newEdges = [...(newEdges ?? []), releProtecaoTosubstationTrafoEdge];
        } else if (substation.hasSubstation && !substation.hasTransformer && !substation.hasDisjuntor) {
        }

        setNodes(newNodes);
        setEdges(newEdges);
    }, [selectedDashboardUsina, ugData, releProtecaoData, substation]);

    // Atualiza o fitView sempre que os nodes forem carregados/atualizados
    useEffect(() => {
        if (nodes.length > 0) {
            setTimeout(() => {
                fitView({ padding: 0.04, duration: 700, includeHiddenNodes: true });
            }, 100);
        }
    }, [nodes, fitView]);

    const [openCommandModal, setOpenCommandModal] = useState(false);

    return (
        <Box style={{ width: "100%", height: "100%" }}>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={nodeTypes}

                /* Sempre encaixa tudo na tela */
                fitView
                fitViewOptions={{ padding: 0.04, includeHiddenNodes: true }}

                defaultEdgeOptions={{
                    type: "step",
                    style: { stroke: "#FFF", strokeWidth: 1 },
                }}

                /* TRAVAS */
                nodesDraggable={false}
                nodesConnectable={false}

                /* Remove deslize */
                panOnDrag={false}
                panOnScroll={false}

                /* Remove zoom */
                zoomOnScroll={false}
                zoomOnPinch={false}
                zoomOnDoubleClick={false}
            >
                <Background color="#1E2A38" gap={20} />
            </ReactFlow>

            {clickedInversor && (
                <StringsModal
                    currentInversor={clickedInversor}
                    open={openStringsModal}
                    onClose={onCloseStringsModal}
                />
            )}

            {commandModalValues && (
                <CommandsModal
                    areaCode={commandModalValues.areaCode}
                    commands={commandModalValues.commands}
                    equipmentIdentifier={commandModalValues.equipmentIdentifier}
                    onClose={() => setOpenCommandModal(false)}
                    open={openCommandModal}
                    type={commandModalValues.type}
                    usinaCode={commandModalValues.usinaCode}
                    usinaId={commandModalValues.usinaId}
                    statusLabel={commandModalValues.statusLabel}
                />
            )}
        </Box>
    );

}

export default function DiagramaUnifilar(props: DiagramaUnifilarProps) {
    return (
        <ReactFlowProvider>
            <DiagramContent {...props} />
        </ReactFlowProvider>
    );
}